import { ReactElement } from 'react';
import { useLocale } from '@hooks/useLocale';
import type { HitProps } from '@components/algolia/Hit';
import {
  BrandedStoreSliceType,
  BrandedStoreSlice,
} from '@adapters/prismic/types/brandedStorePage';
import ImageComponent from '../BrandedStoreSlices/ImageComponent';
import PhotoGrid from '../BrandedStoreSlices/PhotoGrid';
import BrandValues from '../BrandedStoreSlices/BrandValues';
import CollectionAssortment from '../BrandedStoreSlices/CollectionAssortment';
import HeroPLP from '../BrandedStoreSlices/HeroPLP';
import FeaturedBox from '../BrandedStoreSlices/FeaturedBox';
import ProductGrid from '../ProductGrid';
import CategoryNavCarousel from '../Slices/CategoryNavCarousel';
import { PageType } from '@adapters/prismic/types/slices';
import BestSellerRecommendations from '../BrandedStoreSlices/BestSellerRecommendations';
import AlgonomyPlacementCarousel from '../AlgonomyPlacementCarousel';
import { AlgonomyPlacement } from '@components/global/Recommendations/Loader';
import HeroBanner from '../Slices/HeroBanner';
import PrimaryPromoBoxes from '../Slices/PrimaryPromoBoxes';
import SecondaryPromoBoxes from '../Slices/SecondaryPromoBoxes';
import BlogArticlesGrid from '../BlogArticlesGrid';
import Container from '@components/global/Container';
import CategoryGrid from '../Slices/CategoryGrid';
import { useTranslation } from 'next-i18next';
import EnhancedPlpBanner from '@components/algolia/EnhancedPlpBanner';
import CtaStack from '../Slices/CtaStack';
import { HomepageSlice } from '@adapters/prismic/types/homepage';
import SeoContentAccordion from '_components/PLP/SeoContentAccordion';
import mapSeoAccordionData from '@adapters/prismic/utils/mapSeoAccordionData';
import SeoContentComponent from '@components/global/SeoContentComponent/SeoContentComponent';
import {
  FEATURE_FLAG_SEO_ACCORDION,
  FEATURE_FLAG_MAIN_HERO,
  FEATURE_FLAG_LEARN_STRIP,
  FEATURE_FLAG_PROMO_TILES,
  FEATURE_FLAG_BRAND_TILES,
  FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID,
  FEATURE_FLAG_IMAGE_HOTSPOT,
} from '@config/feature-flag';
import MainHero from '_components/MainHero';
import mapMainHeroData from '@adapters/prismic/utils/mapMainHeroData';
import LearnStrip from '_components/LearnStrip';
import mapLearnStripData from '@adapters/prismic/utils/mapLearnStripData';
import PromoTiles from '_components/PromoTiles';
import mapPromoTilesData from '@adapters/prismic/utils/mapPromoTilesData';
import BrandTiles from '_components/BrandTiles';
import mapBrandTilesData from '@adapters/prismic/utils/mapBrandTilesData';
import ActivityNavigationGrid from '_components/ActivityNavigationGrid';
import { transformActivityNavigationGrid } from '@adapters/prismic/transforms/activityNavigationGrid';
import ImageHotspot from '_components/ImageHotspot';
import {
  transformImageHotspot,
  ImageHotspotSlice,
} from '@adapters/prismic/transforms/imageHotspot';
import HomepageValueProps from '../BrandedStoreSlices/HomepageValueProps/HomepageValueProps';
import { InteractionLocation } from '@utils/gtm/ga4/trackPromotionEvents';
import MarkdownCopy from '../Slices/MarkdownCopy';

import styles from '../CategoryPageSliceZone/CategoryPageSliceZone.module.scss';

const CategoryPageSliceHandler = ({
  slice,
  pageType,
  sliceIndex,
  recommendations,
  imageHotspotProducts,
}: {
  slice: BrandedStoreSlice | HomepageSlice;
  pageType: PageType;
  sliceIndex: number;
  recommendations?: AlgonomyPlacement[];
  /** ED-345: SKU->product data resolved in getStaticProps for image_hotspot slices. */
  imageHotspotProducts?: Record<string, HitProps>;
}): ReactElement | null => {
  const locale = useLocale();
  const { t } = useTranslation('common');

  const bestSellersRecommendations = recommendations?.filter(
    (recommendation) => recommendation.name === 'category_page.intro1_rr'
  );

  const collectionAssortmentRecommendations1 = recommendations?.filter(
    (recommendation) => recommendation.name === 'category_page.intro2_rr'
  );

  const collectionAssortmentRecommendations2 = recommendations?.filter(
    (recommendation) => recommendation.name === 'category_page.intro3_rr'
  );

  // `image_hotspot` isn't yet a member of the generated BrandedStoreSlice /
  // HomepageSlice union (it's registered with Prismic in Task 8), so it can't
  // be a `switch` case on `slice?.slice_type` without breaking that union's
  // exhaustiveness typing for every other case. Handled as an early return,
  // with the type assertion scoped to just this branch.
  if ((slice as { slice_type?: string })?.slice_type === 'image_hotspot') {
    if (!FEATURE_FLAG_IMAGE_HOTSPOT) {
      return null;
    }
    const imageHotspotData = transformImageHotspot(
      slice as unknown as ImageHotspotSlice,
      imageHotspotProducts
    );
    return imageHotspotData ? (
      <ImageHotspot
        tabs={imageHotspotData.tabs}
        heading={imageHotspotData.heading}
        priority={sliceIndex === 0}
      />
    ) : null;
  }

  switch (slice?.slice_type) {
    case BrandedStoreSliceType.heroBanner:
      return (
        <HeroBanner
          mediaType={slice.primary?.media_type}
          mobileImage={slice.primary?.mobile_image}
          desktopImage={slice.primary?.desktop_image}
          mobileVideo={slice.primary?.mobile_video}
          desktopVideo={slice.primary?.desktop_video}
          videoLabel={slice.primary?.video_alt_text}
          url={slice.primary?.url}
          blackText={slice.primary?.black_text}
          redText={slice.primary?.red_text}
          redTextPlacement={slice.primary?.red_text_placement}
          redTextNewLine={slice.primary?.red_text_new_line}
          bodyCopy={slice.primary?.body_copy}
          ctaText={slice.primary?.cta_text}
          ctaType={slice.primary?.cta_type}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.mainHero:
      return FEATURE_FLAG_MAIN_HERO ? (
        <MainHero data={mapMainHeroData(slice.primary)} />
      ) : null;
    case BrandedStoreSliceType.learnStrip:
      return FEATURE_FLAG_LEARN_STRIP ? (
        <LearnStrip {...mapLearnStripData(slice.primary, slice.items)} />
      ) : null;
    case BrandedStoreSliceType.promoTiles:
      return FEATURE_FLAG_PROMO_TILES ? (
        <PromoTiles {...mapPromoTilesData(slice.primary, slice.items)} />
      ) : null;
    case BrandedStoreSliceType.brandTiles:
      return FEATURE_FLAG_BRAND_TILES ? (
        <BrandTiles
          {...mapBrandTilesData(slice.primary, slice.items)}
          priority={sliceIndex === 0}
        />
      ) : null;
    case BrandedStoreSliceType.activityNavigationGrid: {
      if (!FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID) {
        return null;
      }
      const activityNavigationGridData = transformActivityNavigationGrid(
        slice,
        locale
      );
      return activityNavigationGridData ? (
        <ActivityNavigationGrid
          heading={activityNavigationGridData.heading}
          body={activityNavigationGridData.body}
          items={activityNavigationGridData.items}
        />
      ) : null;
    }
    case 'primary_promo_boxes':
      return (
        <PrimaryPromoBoxes
          title={slice.primary?.title}
          centerTitle={slice.primary?.center_align}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'secondary_promo_boxes':
      return (
        <SecondaryPromoBoxes
          title={slice.primary.title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'featured_blog_posts':
      return (
        <Container>
          <BlogArticlesGrid
            blogPosts={slice.blog_posts}
            title={slice?.primary?.heading}
            linkTitle={t('recent.articles.button')}
          />
        </Container>
      );
    case BrandedStoreSliceType.collectionAssortment:
      return (
        <CollectionAssortment
          locale={locale}
          primary={slice?.primary}
          items={slice?.items}
          products={
            slice?.primary?.collection_path === 'category_page.intro2_rr'
              ? collectionAssortmentRecommendations1?.[0]?.items
              : collectionAssortmentRecommendations2?.[0]?.items
          }
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.photoGrid:
      return (
        <PhotoGrid
          locale={locale}
          items={slice?.items}
          primary={slice?.primary}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.categoryNavCarousel:
      return (
        <CategoryNavCarousel
          title={slice.primary.title}
          items={slice.items}
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.brandValues:
      return (
        <BrandValues
          primary={slice?.primary}
          items={slice?.items}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.heroPLP:
      return <HeroPLP primary={slice?.primary} />;
    case BrandedStoreSliceType.featuredBox:
      return (
        <FeaturedBox
          primary={slice?.primary}
          locale={locale}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.productGridWithTooltips:
      return (
        <ProductGrid
          title={slice.primary.title}
          id={slice?.primary?.id}
          body={slice?.primary?.copy?.[0]?.text}
          images={slice?.items}
          cta={{
            text: slice.primary.cta_text,
            url: slice.primary.cta_url,
            importance: slice.primary.cta_importance,
          }}
          altPosition={slice.primary?.alt_position}
          icon={slice.primary?.icon}
          socialProof={{
            top: slice?.primary?.social_proof_top?.[0]?.text ?? '',
            bottom: slice?.primary?.social_proof_bottom?.[0]?.text ?? '',
            topSize: slice.primary.social_proof_top_size,
            bottomSize: slice.primary.social_proof_bottom_size,
          }}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.imageComponent:
      return (
        <ImageComponent
          primary={slice?.primary}
          items={slice?.items}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.bestSellerRecommendations:
      return (
        <BestSellerRecommendations
          featuredBestSellersRecommendations={bestSellersRecommendations}
          sliceIndex={sliceIndex}
        />
      );
    case 'algonomy_placement':
      return (
        <AlgonomyPlacementCarousel
          placement={recommendations?.find(
            (recommendation) =>
              recommendation.name === slice.primary?.placement_name
          )}
          coOpBrand={slice.primary?.co_op_brand}
        />
      );
    case BrandedStoreSliceType.categoryGrid:
      return (
        <CategoryGrid
          title={slice?.primary?.title}
          items={slice?.items}
          mobileScroll={slice?.primary?.mobile_scroll}
        />
      );
    case BrandedStoreSliceType.imageWithText:
      return (
        <EnhancedPlpBanner
          title={slice?.primary?.eplp_title}
          copy={slice?.primary?.eplp_copy}
          markdownCopy={slice?.primary?.markdown_copy}
          ctaText={slice?.primary?.eplp_cta_text}
          ctaUrl={slice?.primary?.eplp_cta_link}
          desktopImage={slice?.primary?.eplp_desktop_image}
          mobileImage={slice?.primary?.eplp_mobile_image}
          textOnRight={slice?.primary?.text_on_right}
          noH1
          sliceIndex={sliceIndex}
          interactionLocation={InteractionLocation.ImageText}
        />
      );
    case BrandedStoreSliceType.ctaStack:
      return (
        <CtaStack
          primary={slice?.primary}
          items={slice?.items}
          locale={locale}
        />
      );
    case 'seo_content_component':
      return FEATURE_FLAG_SEO_ACCORDION ? (
        <SeoContentAccordion data={mapSeoAccordionData(slice?.primary)} />
      ) : (
        <SeoContentComponent data={slice?.primary} />
      );
    case BrandedStoreSliceType.valueProps:
      return (
        <HomepageValueProps primary={slice?.primary} items={slice?.items} />
      );
    case BrandedStoreSliceType.markdownCopy:
      return (
        <MarkdownCopy
          text={slice?.primary?.copy?.[0]?.text}
          className={styles.markdown}
        />
      );
    default:
      return null;
  }
};

export default CategoryPageSliceHandler;
